import { expectingDeleteWithActiveIntegrationOnAcceptChanges } from "lib/billingDocument";

interface BillingDocumentChangeFlags {
  hasFinancialChanges?: boolean;
  hasCosmeticChanges?: boolean;
}

type BillingDocumentAcceptFields = BillingDocumentChangeFlags &
  Pick<BillingDocument, "hasFinancialChanges" | "id" | "integrationStatus">;

type LedgerEntriesByDocumentId = Record<
  string,
  { ledgerEntry: Pick<BillingLedgerEntry, "status"> }[]
>;

export const billingDocumentHasChangesToReject = (
  billingDocument: BillingDocumentChangeFlags,
): boolean =>
  Boolean(
    billingDocument.hasFinancialChanges || billingDocument.hasCosmeticChanges,
  );

export const billingDocumentCanBulkAcceptWithoutReversal = (
  billingDocument: BillingDocumentAcceptFields,
  paymentIds: string[] | null | undefined,
  ledgerEntriesByDocumentId: LedgerEntriesByDocumentId,
): boolean =>
  billingDocumentHasChangesToReject(billingDocument) &&
  !paymentIds?.length &&
  !expectingDeleteWithActiveIntegrationOnAcceptChanges(
    billingDocument,
    ledgerEntriesByDocumentId,
  );

export const bulkAcceptWithoutReversalBlockedCounts = (
  billingDocuments: BillingDocumentAcceptFields[],
  paymentIdsByBillingDocumentId: Record<string, string[] | null | undefined>,
  ledgerEntriesByDocumentId: LedgerEntriesByDocumentId,
) => {
  let withPayment = 0;
  let wouldDeleteExported = 0;
  billingDocuments.forEach(billingDocument => {
    if (!billingDocumentHasChangesToReject(billingDocument)) {
      return;
    }
    if (paymentIdsByBillingDocumentId[billingDocument.id]?.length) {
      withPayment += 1;
    } else if (
      expectingDeleteWithActiveIntegrationOnAcceptChanges(
        billingDocument,
        ledgerEntriesByDocumentId,
      )
    ) {
      wouldDeleteExported += 1;
    }
  });
  return { withPayment, wouldDeleteExported };
};

export const bulkAcceptWithoutReversalBlockedMessage = ({
  withPayment,
  wouldDeleteExported,
}: {
  withPayment: number;
  wouldDeleteExported: number;
}): string => {
  const reasons = [];
  if (withPayment) {
    reasons.push(
      `${withPayment} with a payment assigned (remove the payment in Xero first)`,
    );
  }
  if (wouldDeleteExported) {
    reasons.push(
      `${wouldDeleteExported} where accepting would remove a document exported to Xero (void or delete it in Xero first)`,
    );
  }
  return `None of the selected documents can be accepted in bulk without a reversal: ${reasons.join(", ")}.`;
};
